import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axios/axiosInstance';
import { featuresForCompanyContext } from 'constants/companyFeatures';

const EMPTY_INVITE = { email: '', companyName: '' };
const EMPTY_ADD = { legalName: '', email: '', phone: '' };

const MyCompanies = () => {
    const role = localStorage.getItem('userRole') || '';
    const canManagePortfolio = role === 'Accountant' || role === 'Reseller';
    const [companies, setCompanies] = useState(() => {
        try { return JSON.parse(localStorage.getItem('userCompanies') || '[]'); }
        catch { return []; }
    });
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [tab, setTab] = useState('list'); // list | invite | add
    const [inviteForm, setInviteForm] = useState(EMPTY_INVITE);
    const [addForm, setAddForm] = useState(EMPTY_ADD);
    const [saving, setSaving] = useState(false);
    const [invites, setInvites] = useState([]);
    const currentCompanyId = localStorage.getItem('companyId');

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const refreshCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/auth/my-companies');
            const list = Array.isArray(res.data) ? res.data : (res.data?.companies || []);
            setCompanies(list);
            localStorage.setItem('userCompanies', JSON.stringify(list));
            // If no current company and we have at least one, set it
            if (!localStorage.getItem('companyId') && list.length > 0) {
                localStorage.setItem('companyId', list[0].id);
            }
        } catch {
            // Fall back to localStorage list already loaded
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchInvites = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/invites/list');
            const all = Array.isArray(res.data) ? res.data : [];
            setInvites(all.filter(i => i.inviteType === 'company'));
        } catch {
            setInvites([]);
        }
    }, []);

    useEffect(() => {
        refreshCompanies();
        if (canManagePortfolio) fetchInvites();
    }, [refreshCompanies, fetchInvites, canManagePortfolio]);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!canManagePortfolio) { showToast('Employer accounts cannot invite other companies.', 'error'); return; }
        if (!inviteForm.email.trim()) { showToast('Email is required.', 'error'); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/invites/send', {
                email: inviteForm.email.trim(),
                inviteType: 'company',
                companyName: inviteForm.companyName.trim() || null,
            });
            showToast(`Invite sent to ${inviteForm.email}`);
            setInviteForm(EMPTY_INVITE);
            setTab('list');
            fetchInvites();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to send invite.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!canManagePortfolio) { showToast('Employer accounts cannot add other companies.', 'error'); return; }
        if (!addForm.legalName.trim()) { showToast('Company name is required.', 'error'); return; }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/auth/add-company', {
                legalName: addForm.legalName.trim(),
                email: addForm.email.trim() || null,
                phone: addForm.phone.trim() || null,
            });
            const created = res.data;
            showToast(`Company "${created.legalName || addForm.legalName}" added.`);
            setAddForm(EMPTY_ADD);
            setTab('list');
            await refreshCompanies();
            // Switch into the new company
            if (created.id) {
                try {
                    const sw = await axiosInstance.post('/auth/switch-company', { companyId: created.id });
                    localStorage.setItem('authToken', sw.data.token);
                    localStorage.setItem('companyId', created.id);
                    const role = localStorage.getItem('userRole') || sw.data.role;
                    localStorage.setItem(
                        'userFeatures',
                        JSON.stringify(featuresForCompanyContext(role, sw.data.features))
                    );
                    window.location.href = '/dashboard';
                    return;
                } catch {
                    showToast('Company added, but switch failed. Open it from the list.', 'error');
                }
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to add company.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleSwitch = async (companyId) => {
        if (companyId === currentCompanyId) return;
        try {
            const res = await axiosInstance.post('/auth/switch-company', { companyId });
            localStorage.setItem('authToken', res.data.token);
            localStorage.setItem('companyId', companyId);
            const role = localStorage.getItem('userRole') || res.data.role;
            localStorage.setItem(
                'userFeatures',
                JSON.stringify(featuresForCompanyContext(role, res.data.features))
            );
            window.location.href = '/dashboard';
        } catch {
            showToast('Failed to switch company.', 'error');
        }
    };

    const displayName = (c) => c.tradeName || c.legalName || c.id;

    return (
        <div style={{ padding: '0 4px' }}>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: 0 }}>
                        {canManagePortfolio ? 'My Companies' : 'My Company'}
                    </h2>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>
                        {canManagePortfolio
                            ? 'Invite or add client companies. Switch between them anytime from the header.'
                            : 'Your employer account is linked to one company.'}
                    </p>
                </div>
                {canManagePortfolio && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={() => setTab(tab === 'invite' ? 'list' : 'invite')}
                            style={{
                                background: tab === 'invite' ? '#fff' : 'var(--brand-primary, #1B75F0)',
                                color: tab === 'invite' ? '#1B75F0' : '#fff',
                                border: tab === 'invite' ? '1px solid #1B75F0' : 'none',
                                borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            {tab === 'invite' ? 'Cancel' : '✉ Invite Company'}
                        </button>
                        <button
                            onClick={() => setTab(tab === 'add' ? 'list' : 'add')}
                            style={{
                                background: tab === 'add' ? '#fff' : '#1e293b',
                                color: tab === 'add' ? '#1e293b' : '#fff',
                                border: tab === 'add' ? '1px solid #1e293b' : 'none',
                                borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            {tab === 'add' ? 'Cancel' : '+ Add Company'}
                        </button>
                    </div>
                )}
            </div>

            {canManagePortfolio && tab === 'invite' && (
                <form onSubmit={handleInvite} style={{
                    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                    padding: 24, marginBottom: 24, maxWidth: 520,
                }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Invite a Client Company</h3>
                    <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                        They receive an email to register. Once complete, the company is linked to your account.
                    </p>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Company Admin Email *</label>
                    <input
                        type="email" required value={inviteForm.email}
                        onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="admin@clientcompany.com"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Company Name (optional)</label>
                    <input
                        type="text" value={inviteForm.companyName}
                        onChange={e => setInviteForm(f => ({ ...f, companyName: e.target.value }))}
                        placeholder="Acme Corp"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 16, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <button type="submit" disabled={saving} style={{
                        background: 'var(--brand-primary, #1B75F0)', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
                    }}>
                        {saving ? 'Sending…' : 'Send Invite'}
                    </button>
                </form>
            )}

            {canManagePortfolio && tab === 'add' && (
                <form onSubmit={handleAdd} style={{
                    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
                    padding: 24, marginBottom: 24, maxWidth: 520,
                }}>
                    <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Add Company Directly</h3>
                    <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                        Creates the company and links it to you immediately. You can invite their admin later.
                    </p>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Legal Name *</label>
                    <input
                        type="text" required value={addForm.legalName}
                        onChange={e => setAddForm(f => ({ ...f, legalName: e.target.value }))}
                        placeholder="Acme Corporation"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Company Email</label>
                    <input
                        type="email" value={addForm.email}
                        onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="info@acme.com"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 14, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Phone</label>
                    <input
                        type="text" value={addForm.phone}
                        onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 16, fontSize: 14, boxSizing: 'border-box' }}
                    />
                    <button type="submit" disabled={saving} style={{
                        background: '#1e293b', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 13,
                    }}>
                        {saving ? 'Creating…' : 'Create & Link Company'}
                    </button>
                </form>
            )}

            {/* Company list */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 24 }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                    {canManagePortfolio ? `Linked Companies (${companies.length})` : 'Your Company'}
                </div>
                {loading ? (
                    <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
                ) : companies.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🏢</div>
                        <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>No companies yet</div>
                        <div style={{ fontSize: 13, color: '#94a3b8' }}>
                            {canManagePortfolio
                                ? 'Invite a client or add a company to get started.'
                                : 'Contact support if your company is missing.'}
                        </div>
                    </div>
                ) : (
                    companies.map(c => {
                        const isCurrent = c.id === currentCompanyId;
                        return (
                            <div key={c.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '14px 18px', borderBottom: '1px solid #f8fafc',
                                background: isCurrent ? '#F0F7FF' : '#fff',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 10,
                                        background: isCurrent ? 'linear-gradient(135deg, #1B75F0, #5B9BF5)' : '#f1f5f9',
                                        color: isCurrent ? '#fff' : '#64748b',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: 14,
                                    }}>
                                        {displayName(c).charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{displayName(c)}</div>
                                        {isCurrent && <div style={{ fontSize: 11, color: '#1B75F0', fontWeight: 600 }}>Current</div>}
                                    </div>
                                </div>
                                {!isCurrent && (
                                    <button
                                        onClick={() => handleSwitch(c.id)}
                                        style={{
                                            background: '#fff', border: '1px solid #1B75F0', color: '#1B75F0',
                                            borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                        }}
                                    >
                                        Switch
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pending company invites */}
            {invites.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
                        Company Invites Sent
                    </div>
                    {invites.map(inv => (
                        <div key={inv.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '12px 18px', borderBottom: '1px solid #f8fafc', fontSize: 13,
                        }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{inv.email}</div>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                    Sent {new Date(inv.createdAt).toLocaleDateString()} · Expires {new Date(inv.expiresAt).toLocaleDateString()}
                                </div>
                            </div>
                            <span style={{
                                fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                                background: inv.status === 'pending' ? '#fef3c7' : inv.status === 'used' ? '#dcfce7' : '#f3f4f6',
                                color: inv.status === 'pending' ? '#b45309' : inv.status === 'used' ? '#15803d' : '#6b7280',
                            }}>
                                {inv.status}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyCompanies;
